import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import Button from './Button';
import RegionDropdown from './RegionDropdown';
import LocationsDropdown from './LocationsDropdown';
import MegaMenu from './MegaMenu';
import HamburgerMenu from './HamburgerMenu';
import { useRegion } from '../../context/RegionContext';
import { useHeader } from '../../context/HeaderContext';
import { useLocationModal } from '../../context/LocationModalContext';
import { useWaitlist } from '../../context/WaitlistContext';

export default function Header() {
  const { useBlurredBackground } = useHeader();
  const { openLocationModal } = useLocationModal();
  const { openWaitlist } = useWaitlist();
  // Format region name for display
  const formatRegionName = (region) => {
    if (!region) return 'Select Region';
    
    return region
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null); // 'menu' or 'locations'
  const [menuCategories, setMenuCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const { selectedRegion, changeRegion } = useRegion();
  
  // Prevent body scroll when mobile menu is open and reset submenu when closed
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
      // Reset submenu when menu closes
      setActiveSubmenu(null);
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMenuOpen]);
  
  // Fetch menu categories when menu submenu opens
  useEffect(() => {
    async function fetchMenuCategories() {
      if (activeSubmenu === 'menu' && menuCategories.length === 0) {
        try {
          const { getMenuCategories } = await import('../../lib/sanity');
          const categories = await getMenuCategories();
          setMenuCategories(categories);
        } catch (error) {
          console.error('Error fetching menu categories:', error);
        }
      }
    }
    fetchMenuCategories();
  }, [activeSubmenu, menuCategories.length]);
  
  // Fetch locations when locations submenu opens
  useEffect(() => {
    async function fetchLocations() {
      if (activeSubmenu === 'locations' && locations.length === 0) {
        try {
          const { getLocations } = await import('../../lib/sanity');
          const fetchedLocations = await getLocations();
          setLocations(fetchedLocations);
        } catch (error) {
          console.error('Error fetching locations:', error);
        }
      }
    }
    fetchLocations();
  }, [activeSubmenu, locations.length]);
  
  return (
    <header className={`py-4 left-0 right-0 z-50 ${useBlurredBackground ? 'bg-brand-cream shadow-sm relative' : 'bg-transparent absolute top-0'}`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center">
          {/* Logo - Left */}
          <div>
            <Link to="/">
              <img src="/images/oph-logo.svg" alt="Original Pancake House" className="h-7 min-[480px]:h-10 xl:h-12" />
            </Link>
          </div>
          
          {/* Desktop Navigation - Center with equal margins */}
          <nav className="hidden xl:flex xl:mx-auto space-x-5 2xl:space-x-8 items-center">
            <MegaMenu />
            <LocationsDropdown />
            <button 
              onClick={openWaitlist}
              className="text-brand-navy hover:text-brand-orange transition-colors font-normal"
            >
              WAITLIST
            </button>
            <Link to="/loyalty" className="text-brand-navy hover:text-brand-orange transition-colors">LOYALTY</Link>
            <Link to="/contact" className="text-brand-navy hover:text-brand-orange transition-colors">CONTACT</Link>
            <HamburgerMenu />
          </nav>
          
          {/* Mobile Quick Access Menu (480px and below) */}
          <div className="flex xl:hidden items-center ml-auto">
            {/* Quick Access Buttons - Only show at 480px and below */}
            <div className="flex min-[480px]:hidden items-center divide-x divide-gray-300">
              <Link 
                to="/menu"
                className="flex flex-col items-center text-brand-navy hover:text-brand-orange transition-colors px-3"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mb-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <span className="text-[10px] font-medium">Menu</span>
              </Link>
              
              <Link 
                to="/locations"
                className="flex flex-col items-center text-brand-navy hover:text-brand-orange transition-colors px-3"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mb-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-[10px] font-medium">Locations</span>
              </Link>
              
              <button 
                onClick={openWaitlist}
                className="flex flex-col items-center text-brand-navy hover:text-brand-orange transition-colors px-3"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mb-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-[10px] font-medium">Waitlist</span>
              </button>
              
              {/* Mobile Menu Button (Hamburger) - Now part of the group */}
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="flex flex-col items-center text-brand-navy hover:text-brand-orange transition-colors pl-3"
                aria-label="Toggle menu"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mb-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                <span className="text-[10px] font-medium">More</span>
              </button>
            </div>
            
            {/* Mobile Menu Button (Hamburger) - Show at 480px-1279px */}
            <button 
              className="min-[480px]:block hidden text-brand-navy p-2 -mr-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
          
          {/* Location Selector and Order Button - Right */}
          <div className="hidden xl:flex items-center space-x-2 2xl:space-x-4">
            <RegionDropdown 
              selectedRegion={selectedRegion}
              onRegionChange={changeRegion}
            />
            <Button 
              onClick={openLocationModal}
              variant="solid" 
              color="navy"
              className="text-sm 2xl:text-base py-2 2xl:py-2.5 px-3 2xl:px-4"
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 2xl:h-5 2xl:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              }
            >
              Order Online
            </Button>
          </div>
        </div>
        
        {/* Mobile Navigation - Full Screen Overlay (Mobile Only) */}
        {createPortal(
          <AnimatePresence>
            {isMenuOpen && (
              <>
                {/* Backdrop */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="xl:hidden fixed inset-0 bg-black bg-opacity-50 z-[60]"
                  onClick={() => setIsMenuOpen(false)}
                />
                
                {/* Slide-in Menu */}
                <motion.div
                  initial={{ x: '100%' }}
                  animate={{ x: 0 }}
                  exit={{ x: '100%' }}
                  transition={{ type: 'tween', duration: 0.3 }}
                  className="xl:hidden fixed top-0 right-0 bottom-0 w-full sm:w-80 bg-white z-[70] shadow-2xl overflow-hidden"
                >
                  <div className="relative h-full">
                    {/* Main Menu */}
                    <motion.div
                      animate={{ x: activeSubmenu ? '-100%' : 0 }}
                      transition={{ type: 'tween', duration: 0.3 }}
                      className="absolute inset-0 flex flex-col overflow-y-auto"
                    >
                    {/* Header with Close Button */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-200">
                      <img src="/images/oph-logo.svg" alt="Original Pancake House" className="h-10" loading="lazy" />
                      <button
                        onClick={() => setIsMenuOpen(false)}
                        className="p-2 -mr-2 text-brand-navy hover:text-brand-orange transition-colors"
                        aria-label="Close menu"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    
                    {/* Primary Actions */}
                    <div className="p-6 space-y-3">
                      <Button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsMenuOpen(false);
                          openLocationModal();
                        }}
                        variant="solid" 
                        color="navy"
                        className="w-full justify-center text-base py-3"
                        icon={
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                          </svg>
                        }
                      >
                        Order Online
                      </Button>
                      
                      <Button 
                        onClick={() => {
                          setIsMenuOpen(false);
                          openWaitlist();
                        }}
                        variant="outline" 
                        color="navy"
                        className="w-full justify-center text-base py-3"
                        icon={
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        }
                      >
                        Join Waitlist
                      </Button>
                    </div>
                    
                    {/* Divider */}
                    <div className="border-t border-gray-200" />
                    
                    {/* Main Navigation Links */}
                    <nav className="flex-1 p-6">
                      <div className="space-y-1">
                        <button 
                          onClick={() => setActiveSubmenu('menu')}
                          className="w-full flex items-center justify-between py-4 px-4 text-brand-navy hover:bg-brand-cream rounded-lg transition-all group"
                        >
                          <div className="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-3 text-brand-navy group-hover:text-brand-orange transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                            <span className="font-medium text-base">Menu</span>
                          </div>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 group-hover:text-brand-orange transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                        
                        <button 
                          onClick={() => setActiveSubmenu('locations')}
                          className="w-full flex items-center justify-between py-4 px-4 text-brand-navy hover:bg-brand-cream rounded-lg transition-all group"
                        >
                          <div className="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-3 text-brand-navy group-hover:text-brand-orange transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="font-medium text-base">Locations</span>
                          </div>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 group-hover:text-brand-orange transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                        
                        <Link 
                          to="/loyalty"
                          onClick={() => setIsMenuOpen(false)}
                          className="flex items-center justify-between py-4 px-4 text-brand-navy hover:bg-brand-cream rounded-lg transition-all group"
                        >
                          <div className="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-3 text-brand-navy group-hover:text-brand-orange transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                            </svg>
                            <span className="font-medium text-base">Loyalty Program</span>
                          </div>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 group-hover:text-brand-orange transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                        
                        <Link 
                          to="/gift-cards"
                          onClick={() => setIsMenuOpen(false)}
                          className="flex items-center justify-between py-4 px-4 text-brand-navy hover:bg-brand-cream rounded-lg transition-all group"
                        >
                          <div className="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-3 text-brand-navy group-hover:text-brand-orange transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="font-medium text-base">Gift Cards</span>
                          </div>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 group-hover:text-brand-orange transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                        
                        <Link 
                          to="/about"
                          onClick={() => setIsMenuOpen(false)}
                          className="flex items-center justify-between py-4 px-4 text-brand-navy hover:bg-brand-cream rounded-lg transition-all group"
                        >
                          <div className="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-3 text-brand-navy group-hover:text-brand-orange transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="font-medium text-base">About Us</span>
                          </div>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 group-hover:text-brand-orange transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                        
                        <Link 
                          to="/contact"
                          onClick={() => setIsMenuOpen(false)}
                          className="flex items-center justify-between py-4 px-4 text-brand-navy hover:bg-brand-cream rounded-lg transition-all group"
                        >
                          <div className="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-3 text-brand-navy group-hover:text-brand-orange transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            <span className="font-medium text-base">Contact</span>
                          </div>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 group-hover:text-brand-orange transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                      </div>
                    </nav>
                    
                    {/* Footer Links */}
                    <div className="p-6 border-t border-gray-200 bg-gray-50">
                      <div className="space-y-3">
                        <a
                          href="https://bte-management-company.r365hire.com/account"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center text-sm text-gray-600 hover:text-brand-navy transition-colors"
                        >
                          <span>Careers</span>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                        <a
                          href="/oph-nutritional-info.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center text-sm text-gray-600 hover:text-brand-navy transition-colors"
                        >
                          <span>Nutritional Information</span>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                        <a
                          href="https://iloveoph.gearnw.com/index_products.php"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center text-sm text-gray-600 hover:text-brand-navy transition-colors"
                        >
                          <span>Shop Apparel</span>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    </div>
                    </motion.div>
                    
                    {/* Menu Categories Submenu */}
                    <motion.div
                      initial={{ x: '100%' }}
                      animate={{ x: activeSubmenu === 'menu' ? 0 : '100%' }}
                      transition={{ type: 'tween', duration: 0.3 }}
                      className="absolute inset-0 bg-white flex flex-col overflow-y-auto"
                    >
                      {/* Back and Close Buttons */}
                      <div className="flex items-center justify-between p-6 pb-4">
                        <button
                          onClick={() => setActiveSubmenu(null)}
                          className="flex items-center py-2 px-3 text-brand-navy hover:bg-brand-cream rounded-lg transition-all font-medium"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                          Back
                        </button>
                        
                        <button
                          onClick={() => setIsMenuOpen(false)}
                          className="p-2 -mr-2 text-brand-navy hover:text-brand-orange transition-colors"
                          aria-label="Close menu"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      
                      {/* View All Button */}
                      <div className="px-6 pb-4">
                        <Link
                          to="/menu"
                          onClick={() => {
                            setActiveSubmenu(null);
                            setIsMenuOpen(false);
                          }}
                          className="flex items-center justify-center py-3 px-4 bg-brand-navy text-white rounded-lg hover:bg-opacity-90 transition-all font-medium"
                        >
                          View Full Menu
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                      </div>
                      
                      {/* Categories List */}
                      <div className="flex-1 px-6 pb-6">
                        <div className="space-y-2">
                          {menuCategories.map((category) => (
                            <Link
                              key={category._id}
                              to={`/menu/${category.slug}`}
                              onClick={() => {
                                setActiveSubmenu(null);
                                setIsMenuOpen(false);
                              }}
                              className="flex items-center justify-between py-3 px-4 text-brand-navy hover:bg-brand-cream rounded-lg transition-all"
                            >
                              <div className="flex items-center flex-1 min-w-0">
                                {category.imageUrl ? (
                                  <img 
                                    src={category.imageUrl} 
                                    alt={category.name}
                                    className="w-12 h-12 rounded-full object-cover mr-3 flex-shrink-0"
                                    loading="lazy"
                                  />
                                ) : (
                                  <div className="w-12 h-12 rounded-full bg-gray-200 mr-3 flex-shrink-0 flex items-center justify-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                    </svg>
                                  </div>
                                )}
                                <span className="font-medium truncate">{category.name}</span>
                              </div>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 flex-shrink-0 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                    
                    {/* Locations Submenu */}
                    <motion.div
                      initial={{ x: '100%' }}
                      animate={{ x: activeSubmenu === 'locations' ? 0 : '100%' }}
                      transition={{ type: 'tween', duration: 0.3 }}
                      className="absolute inset-0 bg-white flex flex-col overflow-y-auto"
                    >
                      {/* Back and Close Buttons */}
                      <div className="flex items-center justify-between p-6 pb-4">
                        <button
                          onClick={() => setActiveSubmenu(null)}
                          className="flex items-center py-2 px-3 text-brand-navy hover:bg-brand-cream rounded-lg transition-all font-medium"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                          Back
                        </button>
                        
                        <button
                          onClick={() => setIsMenuOpen(false)}
                          className="p-2 -mr-2 text-brand-navy hover:text-brand-orange transition-colors"
                          aria-label="Close menu"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      
                      {/* View All Button */}
                      <div className="px-6 pb-4">
                        <Link
                          to="/locations"
                          onClick={() => {
                            setActiveSubmenu(null);
                            setIsMenuOpen(false);
                          }}
                          className="flex items-center justify-center py-3 px-4 bg-brand-navy text-white rounded-lg hover:bg-opacity-90 transition-all font-medium"
                        >
                          View All Locations
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                      </div>
                      
                      {/* Locations List */}
                      <div className="flex-1 px-6 pb-6">
                        <div className="space-y-2">
                          {locations.map((location) => (
                            <Link
                              key={location._id}
                              to={`/locations/${location.slug}`}
                              onClick={() => {
                                setActiveSubmenu(null);
                                setIsMenuOpen(false);
                              }}
                              className="flex items-center justify-between py-3 px-4 text-brand-navy hover:bg-brand-cream rounded-lg transition-all"
                            >
                              <div className="flex items-center flex-1 min-w-0">
                                {location.imageUrl ? (
                                  <img 
                                    src={location.imageUrl} 
                                    alt={location.name}
                                    className="w-12 h-12 rounded-full object-cover mr-3 flex-shrink-0"
                                    loading="lazy"
                                  />
                                ) : (
                                  <div className="w-12 h-12 rounded-full bg-gray-200 mr-3 flex-shrink-0 flex items-center justify-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                  </div>
                                )}
                                <span className="font-medium truncate">{location.name}</span>
                              </div>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 flex-shrink-0 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>,
          document.body
        )}
      </div>
    </header>
  );
}
